import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import { axios, queryClient } from '@apps-common/lib'
import { IPortalUnitSearchParamsModel } from '@apps-common/types/management/unit'
import { IPortalUnitSearchModel } from '@apps-common/types/portal/unit'

export const QUERY_KEY_PORTAL_SEARCH_UNITS_INFINITE = (params?: IPortalUnitSearchParamsModel) =>
  [
    'searchPortalUnitsInfinite',
    params?.status,
    params?.site,
    params?.accountNo,
    params?.mobileNo,
    params?.imei,
    params?.isActivated,
    params?.pinnedUnits,
    params?.orderBy,
    params?.skip,
    params?.take,
    params?.hasNoActivation
  ].filter(Boolean)

export const searchPortalUnitsInfinite = async (params: IPortalUnitSearchParamsModel): Promise<IPortalUnitSearchModel[]> =>
  axios.post(`/Unit/SearchUnits`, params).then((res) => res.data?.data)

type Props = {
  enabled: boolean
  model: IPortalUnitSearchParamsModel
  hideOrderUnits?: boolean
}

export const usePortalSearchUnitsInfinite = ({ enabled = true, model }: Props) => {
  const { isLoading, isFetching, isRefetching, isError, data, refetch, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: QUERY_KEY_PORTAL_SEARCH_UNITS_INFINITE(model),
    initialPageParam: 0,
    queryFn: ({ pageParam = 0 }) => searchPortalUnitsInfinite({ ...model, skip: pageParam }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.length < model.take) {
        return null
      }

      return allPages?.reduce((acc, curr) => acc + (curr?.length || 0), 0)
    }
  })

  const refetchOverride = async () => {
    await queryClient.cancelQueries({ queryKey: QUERY_KEY_PORTAL_SEARCH_UNITS_INFINITE(model) })
    queryClient.removeQueries({ queryKey: QUERY_KEY_PORTAL_SEARCH_UNITS_INFINITE(model) })
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY_PORTAL_SEARCH_UNITS_INFINITE(model) })
    refetch()
  }

  useEffect(
    () => () => {
      queryClient.removeQueries({ queryKey: QUERY_KEY_PORTAL_SEARCH_UNITS_INFINITE(model) })
    },
    []
  )

  return { isLoading, isFetching, isRefetching, isError, data, refetch: refetchOverride, fetchNextPage, hasNextPage }
}
